import { PlusIcon } from '@shopify/polaris-icons';
import { useState } from 'react';
import { Banner, BlockStack, Button, Card, InlineStack, TextField } from '@shopify/polaris';
import { useTransferRead, type TransferClient } from './useTransferClient';
export interface DraftItem {
  product_id: number;
  sku: string | null;
  title: string;
  quantity: string;
  from_location_id: number;
  from_location_name: string;
  to_location_id: number | null;
  to_location_name: string | null;
}
interface Stock {
  id: number;
  product_id: number;
  sku: string | null;
  title: string;
  location_id: number;
  code: string;
  quantity: number;
  reserved: number;
  available: number;
  untracked: number;
}
export function StockPicker({
  client,
  warehouse,
  items,
  onAdd,
  disabled,
}: {
  client: TransferClient;
  warehouse: number;
  items: DraftItem[];
  onAdd: (item: DraftItem) => void;
  disabled: boolean;
}) {
  const [query, setQuery] = useState('');
  const [cursor, setCursor] = useState<number | null>(null);
  const [refresh, setRefresh] = useState(0);
  const { data, error } = useTransferRead<{ stock: Stock[]; nextCursor: number | null }>(
    client,
    `/stock?warehouse_id=${warehouse}&q=${encodeURIComponent(query)}${cursor ? '&cursor=' + cursor : ''}`,
    refresh,
  );
  return (
    <Card>
      <BlockStack gap="300">
        <h2>2. Add products and quantities</h2>
        <p>
          Search below, add a product, then enter its planned quantity in the added line. Each line has one product and one source bin (its storage location). Available stock is checked again when shipping. Serial and batch
          goods require exact identity counts and scans before they move.
        </p>
        <TextField
          label="Search SKU, barcode, product or source bin"
          value={query}
          onChange={(value) => {
            setQuery(value);
            setCursor(null);
          }}
          autoComplete="off"
          maxLength={200}
          disabled={disabled}
        />
        {error && (
          <Banner tone="critical">
            {error}
            <Button onClick={() => setRefresh((x) => x + 1)}>Retry stock search</Button>
          </Banner>
        )}
        {!data && !error && <p>Loading source stock…</p>}
        {data && !data.stock.length && <p>No stock lines match this search.</p>}
        {data?.stock.map((row) => (
          <div key={row.id}>
            <strong>
              {row.sku || row.title} · {row.code}
            </strong>
            <p>
              {row.title}. Available: {row.available}. Reserved: {row.reserved}.
              {!row.untracked && ' Serial, batch or expiry checks required. Older stock needs reconciliation before shipping.'}
            </p>
            <Button icon={PlusIcon}
              disabled={
                disabled ||
                items.length >= 500 ||
                items.some((x) => x.product_id === row.product_id && x.from_location_id === row.location_id)
              }
              onClick={() =>
                onAdd({
                  product_id: row.product_id,
                  sku: row.sku,
                  title: row.title,
                  quantity: '1',
                  from_location_id: row.location_id,
                  from_location_name: row.code,
                  to_location_id: null,
                  to_location_name: null,
                })
              }
            >
              Add {row.sku || row.title} from {row.code}
            </Button>
          </div>
        ))}
        <InlineStack gap="200">
          <Button disabled={disabled || !cursor} onClick={() => setCursor(null)}>
            First stock page
          </Button>
          <Button disabled={disabled || !data?.nextCursor} onClick={() => setCursor(data?.nextCursor || null)}>
            More stock
          </Button>
        </InlineStack>
      </BlockStack>
    </Card>
  );
}
